import React from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, ShieldAlert } from 'lucide-react';
import { NoorBadge } from './NoorLogo';
import { useLanguage, Language } from '../contexts/LanguageContext';
type FooterStrings = {
  brandDesc: string;
  serviceHeading: string;
  legalHeading: string;
  howItWorks: string;
  pricing: string;
  faq: string;
  contact: string;
  terms: string;
  privacy: string;
  refund: string;
  accessibility: string;
  requiredNoticeHeading: string;
  requiredNotice: string;
  disclaimerHeading: string;
  disclaimerP1: string;
  disclaimerP2: string;
  disclaimerP3: string;
  rights: string;
};
// EN/ES finalized. HT/VI = best effort, pending native review.
// requiredNotice* fields added 2026-09-22: verbatim statutory notice text
// required by Fla. Stat. § 501.1391 (ch. 2025-82, s.3), effective 2025-07-01,
// verified against the official Florida Senate statute text and the enrolled
// bill (HB 915). Must stay word-for-word identical to the statute.
const T: Record<Language, FooterStrings> = {
  en: {
    brandDesc: 'Noor Passport Services is a DBA of Noor Metabolico LLC. We provide online document preparation software to help U.S. citizens complete their own passport applications — in English, Spanish, Haitian Creole, and Vietnamese.',
    serviceHeading: 'Service',
    legalHeading: 'Legal',
    howItWorks: 'How it Works',
    pricing: 'Pricing',
    faq: 'FAQ',
    contact: 'Contact',
    terms: 'Terms of Service',
    privacy: 'Privacy Policy',
    refund: 'Refund Policy',
    accessibility: 'Accessibility',
    requiredNoticeHeading: 'Required Notice — Florida Statute § 501.1391',
    requiredNotice: 'I AM NOT AN ATTORNEY LICENSED TO PRACTICE LAW AND MAY NOT GIVE LEGAL ADVICE OR ACCEPT FEES FOR LEGAL ADVICE. I AM NOT ACCREDITED TO REPRESENT YOU IN IMMIGRATION MATTERS.',
    disclaimerHeading: 'Legal Disclaimer',
    disclaimerP1: 'Noor Passport Services is not a law firm and does not provide legal advice. Our service is limited to document preparation software.',
    disclaimerP2: 'We are not affiliated with the U.S. Department of State or any other U.S. government agency. Blank government forms are available for free directly from the U.S. Department of State.',
    disclaimerP3: 'Customers are solely responsible for reviewing, signing, and submitting their own application to the appropriate government office. We do not submit applications or interact with the Department of State on your behalf.',
    rights: 'All rights reserved.'
  },
  es: {
    brandDesc: 'Noor Passport Services es un nombre comercial (DBA) de Noor Metabolico LLC. Ofrecemos software en línea de preparación de documentos para ayudar a ciudadanos de EE. UU. a completar sus propias solicitudes de pasaporte — en inglés, español, creole haitiano y vietnamita.',
    serviceHeading: 'Servicio',
    legalHeading: 'Legal',
    howItWorks: 'Cómo funciona',
    pricing: 'Precios',
    faq: 'Preguntas',
    contact: 'Contacto',
    terms: 'Términos del servicio',
    privacy: 'Política de privacidad',
    refund: 'Política de reembolso',
    accessibility: 'Accesibilidad',
    requiredNoticeHeading: 'Aviso Obligatorio — Estatuto de Florida § 501.1391',
    requiredNotice: 'NO SOY ABOGADO CON LICENCIA PARA EJERCER LA ABOGACÍA Y NO PUEDO DAR ASESORÍA LEGAL NI COBRAR HONORARIOS POR ASESORÍA LEGAL. NO ESTOY ACREDITADO PARA REPRESENTARLO EN ASUNTOS DE INMIGRACIÓN.',
    disclaimerHeading: 'Aviso legal',
    disclaimerP1: 'Noor Passport Services no es un bufete de abogados y no brinda asesoría legal. Nuestro servicio se limita a software de preparación de documentos.',
    disclaimerP2: 'No estamos afiliados al Departamento de Estado de EE. UU. ni a ninguna otra agencia del gobierno de EE. UU. Los formularios oficiales en blanco están disponibles gratis directamente en el Departamento de Estado de EE. UU.',
    disclaimerP3: 'Los clientes son los únicos responsables de revisar, firmar y entregar su propia solicitud en la oficina gubernamental correspondiente. Nosotros no entregamos solicitudes ni actuamos ante el Departamento de Estado en su nombre.',
    rights: 'Todos los derechos reservados.'
  },
  ht: {
    brandDesc: 'Noor Passport Services se yon non komèsyal (DBA) pou Noor Metabolico LLC. Nou bay lojisyèl preparasyon dokiman sou entènèt pou ede sitwayen ameriken ranpli pwòp aplikasyon paspò yo — an anglè, panyòl, kreyòl ayisyen, ak vyetnamyen.',
    serviceHeading: 'Sèvis',
    legalHeading: 'Legal',
    howItWorks: 'Kijan li mache',
    pricing: 'Pri',
    faq: 'Kesyon',
    contact: 'Kontak',
    terms: 'Kondisyon sèvis',
    privacy: 'Politik konfidansyalite',
    refund: 'Politik ranbousman',
    accessibility: 'Aksesibilite',
    requiredNoticeHeading: 'Avi Obligatwa — Lwa Florid § 501.1391',
    requiredNotice: 'MWEN PA YON AVOKA KI GEN LISANS POU PRATIKE LWA EPI MWEN PA KA BAY KONSÈY LEGAL OSWA AKSEPTE ONORÈ POU KONSÈY LEGAL. MWEN PA AKREDITE POU REPREZANTE OU NAN ZAFÈ IMIGRASYON.',
    disclaimerHeading: 'Avètisman legal',
    disclaimerP1: 'Noor Passport Services se pa yon kabinè avoka epi li pa bay konsèy legal. Sèvis nou an limite ak lojisyèl preparasyon dokiman.',
    disclaimerP2: 'Nou pa afilye ak Depatman Deta Etazini ni ak okenn lòt ajans gouvènman ameriken. Fòm gouvènman vid yo disponib gratis dirèkteman nan Depatman Deta Etazini.',
    disclaimerP3: 'Se kliyan yo sèl ki responsab pou revize, siyen, epi soumèt pwòp aplikasyon yo nan biwo gouvènman ki apwopriye a. Nou pa soumèt aplikasyon ni nou pa aji devan Depatman Deta a nan non ou.',
    rights: 'Tout dwa rezève.'
  },
  vi: {
    brandDesc: 'Noor Passport Services là tên giao dịch (DBA) của Noor Metabolico LLC. Chúng tôi cung cấp phần mềm chuẩn bị giấy tờ trực tuyến để giúp công dân Hoa Kỳ tự hoàn thành đơn xin hộ chiếu của mình — bằng tiếng Anh, Tây Ban Nha, Creole Haiti và tiếng Việt.',
    serviceHeading: 'Dịch vụ',
    legalHeading: 'Pháp lý',
    howItWorks: 'Cách hoạt động',
    pricing: 'Bảng giá',
    faq: 'Câu hỏi',
    contact: 'Liên hệ',
    terms: 'Điều khoản dịch vụ',
    privacy: 'Chính sách bảo mật',
    refund: 'Chính sách hoàn tiền',
    accessibility: 'Khả năng tiếp cận',
    requiredNoticeHeading: 'Thông Báo Bắt Buộc — Đạo Luật Florida § 501.1391',
    requiredNotice: 'TÔI KHÔNG PHẢI LÀ LUẬT SƯ CÓ GIẤY PHÉP HÀNH NGHỀ LUẬT VÀ KHÔNG THỂ ĐƯỦA RA TƯ VẤN PHÁP LÝ HOẶC NHẬN PHÍ TƯ VẤN PHÁP LÝ. TÔI KHÔNG ĐƯỢC CÔNG NHẬN ĐỂ ĐẠI DIỆN CHO QUÝ VỊ TRONG CÁC VẤN ĐỀ DI TRÚ.',
    disclaimerHeading: 'Tuyên bố pháp lý',
    disclaimerP1: 'Noor Passport Services không phải là công ty luật và không cung cấp tư vấn pháp lý. Dịch vụ của chúng tôi chỉ giới hạn ở phần mềm chuẩn bị giấy tờ.',
    disclaimerP2: 'Chúng tôi không liên kết với Bộ Ngoại giao Hoa Kỳ hay bất kỳ cơ quan chính phủ Hoa Kỳ nào khác. Các mẫu đơn chính phủ trống có sẵn miễn phí trực tiếp từ Bộ Ngoại giao Hoa Kỳ.',
    disclaimerP3: 'Khách hàng hoàn toàn chịu trách nhiệm xem lại, ký và nộp đơn của chính mình tại cơ quan chính phủ thích hợp. Chúng tôi không nộp đơn hay thay mặt quý vị làm việc với Bộ Ngoại giao.',
    rights: 'Bảo lưu mọi quyền.'
  }
};
export function Footer() {
  const {
    language
  } = useLanguage();
  const t = T[language];
  const navItems = [{
    to: '/how-it-works',
    label: t.howItWorks
  }, {
    to: '/pricing',
    label: t.pricing
  }, {
    to: '/faq',
    label: t.faq
  }, {
    to: '/contact',
    label: t.contact
  }];
  // Real routes now that the three legal pages exist. Accessibility has no
  // dedicated page yet, so it stays a placeholder link.
  const legalLinks: {
    to: string;
    label: string;
  }[] = [{
    to: '/terms-of-service',
    label: t.terms
  }, {
    to: '/privacy-policy',
    label: t.privacy
  }, {
    to: '/refund-policy',
    label: t.refund
  }];
  return <footer className="bg-brand-footerBg text-white pt-20 pb-10 relative z-10" data-id="element-163" data-mp="2dnuxzy51dn32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-164" data-mp="28rw7a3w8jszh">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16" data-id="element-165" data-mp="1hmtgutuijetv">
          {/* Brand */}
          <div className="lg:col-span-2" data-id="element-166" data-mp="1mjjy3ki20ggv">
            <div className="flex items-center gap-2 mb-6" data-id="element-167" data-mp="1mmobn8hesok7">
              <NoorBadge size={40} data-id="element-168" data-mp="17vfnl3t5nb96" />
              <span className="font-heading font-bold text-2xl tracking-tight" data-id="element-169" data-mp="17u4h2w8brped">
                Noor
              </span>
            </div>
            <p className="text-gray-300 text-base leading-relaxed mb-6 max-w-md" data-id="element-170" data-mp="yv0x7q1c35yt">
              {t.brandDesc}
            </p>
            <div className="flex gap-3 text-sm text-gray-400" data-id="element-171" data-mp="12yff6m8u1wew">
              <a href="mailto:info@noorpassportservices.com" className="text-gray-300 hover:text-white transition-colors underline underline-offset-2" data-id="element-172" data-mp="oxwbfrbehcij">
                info@noorpassportservices.com
              </a>
            </div>
          </div>

          {/* Service */}
          <div data-id="element-173" data-mp="2o8w2uqaytyep">
            <h4 className="font-heading font-semibold text-lg mb-6" data-id="element-174" data-mp="2o9fl684j2ot9">
              {t.serviceHeading}
            </h4>
            <ul className="space-y-4" data-id="element-175" data-mp="ckitxbhw6zqt">
              {navItems.map(item => <li key={item.to} data-id="element-176" data-mp="7pllo6sfsuez">
                  <Link to={item.to} className="text-gray-300 hover:text-white transition-colors text-sm underline underline-offset-2" data-id="element-177" data-mp="mcessorsytw3">
                    {item.label}
                  </Link>
                </li>)}
            </ul>
          </div>

          {/* Legal */}
          <div data-id="element-178" data-mp="qdgudc96zi18">
            <h4 className="font-heading font-semibold text-lg mb-6" data-id="element-179" data-mp="qe0e8yiekghw">
              {t.legalHeading}
            </h4>
            <ul className="space-y-4" data-id="element-180" data-mp="1596lfot8ilct">
              {legalLinks.map(item => <li key={item.to} data-id="element-181" data-mp="10d7srp0vih6l">
                  <Link to={item.to} className="text-gray-300 hover:text-white transition-colors text-sm underline underline-offset-2" data-id="element-182" data-mp="372ma4g5rhq55">
                    {item.label}
                  </Link>
                </li>)}
              <li data-id="element-183" data-mp="dv5h99mftipx">
                <a href="#" className="text-gray-300 hover:text-white transition-colors text-sm underline underline-offset-2" data-id="element-184" data-mp="dum7whaus2ni">
                  {t.accessibility}
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Required statutory notice — Fla. Stat. § 501.1391. Must be
            conspicuous and word-for-word identical to the statute text in
            every language the service is offered in. Kept visually distinct
            (accent border) from the general disclaimer below it. */}
        <div className="p-6 md:p-8 bg-white/[0.04] border-2 border-brand-accent/60 rounded-2xl mb-6" data-id="element-185" data-mp="mw3oe0s6a6cq">
          <div className="flex items-start gap-3 mb-3" data-id="element-186" data-mp="1kbr8x89ijpvy">
            <ShieldAlert className="w-5 h-5 text-brand-accent flex-shrink-0 mt-0.5" data-id="element-187" data-mp="1kdap6yv660xq" />
            <h4 className="font-heading font-semibold text-lg text-white" data-id="element-188" data-mp="1fiy0oaeu5zf4">
              {t.requiredNoticeHeading}
            </h4>
          </div>
          <p className="text-sm md:text-base font-semibold tracking-wide text-gray-200 leading-relaxed uppercase" data-id="element-189" data-mp="1rugwvat5ui28">
            {t.requiredNotice}
          </p>
        </div>

        {/* Prominent Legal Disclaimer */}
        <div className="p-6 md:p-8 bg-white/[0.04] border border-white/10 rounded-2xl mb-10" data-id="element-190" data-mp="21t6hhbcfygm6">
          <div className="flex items-start gap-3 mb-4" data-id="element-191" data-mp="26ssw9p7x44x3">
            <AlertCircle className="w-5 h-5 text-brand-accent flex-shrink-0 mt-0.5" data-id="element-192" data-mp="26pwutqwv2y3w" />
            <h4 className="font-heading font-semibold text-lg text-white" data-id="element-193" data-mp="2arrzn5e03tmf">
              {t.disclaimerHeading}
            </h4>
          </div>
          <div className="space-y-3 text-sm md:text-base text-gray-300 leading-relaxed" data-id="element-194" data-mp="2kj4n7ksrnxmn">
            <p data-id="element-195" data-mp="2pir361zzbrz5">{t.disclaimerP1}</p>
            <p data-id="element-196" data-mp="2pjk0kcd1t54m">{t.disclaimerP2}</p>
            <p data-id="element-197" data-mp="2pgec8zc8u79y">{t.disclaimerP3}</p>
          </div>
        </div>

        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4" data-id="element-198" data-mp="2uin6s04rlhe6">
          <p className="text-gray-500 text-sm" data-id="element-199" data-mp="24ty6zf9i70j7">
            © {new Date().getFullYear()} Noor Passport Services DBA Noor
            Metabolico LLC. {t.rights}
          </p>
          <div className="flex gap-4 text-sm text-gray-500" data-id="element-200" data-mp="2ekb384qoki56">
            <span data-id="element-201" data-mp="29sjc9t3m4aei">English</span>
            <span aria-hidden="true" data-id="element-202" data-mp="29s9gfovvz90w">·</span>
            <span data-id="element-203" data-mp="29qga6ap6qqtf">Español</span>
            <span aria-hidden="true" data-id="element-204" data-mp="2njo3c6swzud8">·</span>
            <span data-id="element-205" data-mp="2nicye3nqggkj">Kreyòl</span>
            <span aria-hidden="true" data-id="element-206" data-mp="2nlr49lvlv7wq">·</span>
            <span data-id="element-207" data-mp="2ijtbo11bf0r4">Tiếng Việt</span>
          </div>
        </div>
      </div>
    </footer>;
}